import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { ArrowLeft, ArrowRight, ArrowsClockwise, Check, FileArrowUp } from "@phosphor-icons/react";
import { Link, Redirect, useLocation, useRoute } from "wouter";
import { CAREER_MAX, NEEDS_US_AUTH, RESUME_BYTES, ROLES, checkCareerEmail, checkCareerForm, roleOf, type CareerProblems } from "../../shared/careers.ts";
import { WITHDRAWABLE, type CareerApplication, type CareerForm, type CareerStatus, type ProfileView, type Role } from "../../shared/types.ts";
import { Button, Dialog, EmptyState, Field, Input, Logo, MARK, MarkLoader, Select, StatusBadge, Steps, Textarea, cn, useToast, type Tone } from "../ui/index.ts";
import { ApiError, applyAsGuest, applyToRole, editApplication, errorText, getCareerApplications, getProfile, withdrawApplication } from "../lib/api.ts";
import { logOut, storeCareerResume } from "../lib/supabase.ts";

type Viewer = { id: string; email: string; name?: string } | null | undefined;

const STATUS: Record<CareerStatus, { label: string; tone: Tone }> = {
  submitted: { label: "Submitted", tone: "brand" },
  reviewing: { label: "In review", tone: "amber" },
  interviewing: { label: "Interviewing", tone: "mint" },
  offer: { label: "Offer", tone: "accent" },
  closed: { label: "Closed", tone: "gray" },
  withdrawn: { label: "Withdrawn", tone: "gray" },
};

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const day = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
const wrap = "mx-auto w-full max-w-[1040px] px-4 sm:px-6";
const display = "font-display text-[32px] leading-[1.1] tracking-[-0.03em] text-ink md:text-[40px]";
const heading = "text-[18px] font-bold tracking-[-0.015em] text-ink";
const applyLink =
  "inline-flex h-11 w-full items-center justify-center rounded-control bg-primary px-6 text-[15px] font-semibold tracking-[-0.01em] text-on-primary shadow-brand transition-[background-color] duration-150 hover:bg-primary-hover";

/** The posting's header facts in ATS order, leaving out any the role doesn't set. */
const meta = (r: Role): [string, string][] =>
  (
    [
      ["Location", r.location],
      ["Employment type", r.employmentType],
      ["Department", r.department],
      ["Compensation", r.pay],
      ["Hours", r.hours],
      ["Dates", r.dates],
    ] as [string, string][]
  ).filter(([, v]) => v.trim());

/** Facts in a muted dot-separated line, each one kept whole when the line wraps. */
function Facts({ parts }: { parts: string[] }) {
  return parts
    .filter((p) => p.trim())
    .map((p, i) => (
      <span key={p}>
        {i > 0 && " · "}
        <span className="whitespace-nowrap">{p}</span>
      </span>
    ));
}

/**
 * The public careers pages: the open roles, one posting, and its application form. Open signed in or out, and rendered to
 * static HTML by build.ts. Signed out, Apply opens the same form, which sends the application without an account. Signed
 * in, each page shows the user's own application where there is one.
 */
export function Careers({ user }: { user: Viewer }) {
  const [location] = useLocation();
  const [applying, a] = useRoute("/careers/:slug/apply");
  const [viewing, v] = useRoute("/careers/:slug");
  const [apps, setApps] = useState<CareerApplication[] | null>(null);
  const [down, setDown] = useState(false);

  const load = useCallback(() => {
    setDown(false);
    getCareerApplications().then(setApps, () => setDown(true));
  }, []);

  useEffect(() => {
    setApps(null);
    if (user) load();
  }, [user, load]);

  useEffect(() => {
    scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const before = document.title;
    return () => {
      document.title = before;
    };
  }, []);

  const put = (app: CareerApplication) => setApps((xs) => [app, ...(xs ?? []).filter((x) => x.role !== app.role)]);
  const role = roleOf(applying ? a.slug : viewing ? v.slug : null);
  const mine = role && apps?.find((x) => x.role === role.slug);
  const known = user === null || apps !== null || down;

  useEffect(() => {
    document.title = `${applying && role ? `Apply · ${role.title}` : (role?.title ?? "Careers")} · Headcount`;
  }, [applying, role]);

  let page: ReactNode = null;
  if ((applying || viewing) && !role)
    page = <EmptyState art="lost" title="This job is no longer listed." action={<BackLink href="/careers">All jobs</BackLink>} />;
  else if (applying && role) {
    if (user === null) page = role.open ? <ApplyForm role={role} user={null} existing={null} onDone={put} /> : <Redirect to={`/careers/${role.slug}`} replace />;
    else if (!user || (!known && !down))
      page = (
        <div className="grid min-h-[50dvh] place-items-center">
          <MarkLoader delay={0.3} />
        </div>
      );
    else if (down) page = <Retry onRetry={load} />;
    else if (mine ? mine.status !== "submitted" : !role.open) page = <Redirect to={`/careers/${role.slug}`} replace />;
    else page = <ApplyForm role={role} user={user} existing={mine ?? null} onDone={put} />;
  } else if (role) page = <Posting role={role} app={known || user === undefined ? (mine ?? null) : undefined} onChange={put} />;

  return (
    <div className="flex min-h-dvh flex-col bg-page">
      <Nav user={user} next={location} />
      <main className="flex-1 pb-20">{page ? <div className={cn(wrap, "pt-8 md:pt-12")}>{page}</div> : <JobList apps={apps ?? []} />}</main>
      <footer className="border-t border-line">
        <div className={cn(wrap, "flex flex-wrap items-center gap-x-6 gap-y-2 pb-[max(2rem,env(safe-area-inset-bottom))] pt-6 text-[14px] text-muted")}>
          <span className="mr-auto tabular-nums">© {new Date().getFullYear()} Headcount</span>
          <a href="/careers" className="transition-[color] duration-150 hover:text-ink">
            Careers
          </a>
          <a href="/privacy" className="transition-[color] duration-150 hover:text-ink">
            Privacy
          </a>
          <a href="/terms" className="transition-[color] duration-150 hover:text-ink">
            Terms
          </a>
          <a href="mailto:hello@itsheadcount.com" className="transition-[color] duration-150 hover:text-ink">
            Contact
          </a>
        </div>
      </footer>
    </div>
  );
}

function Nav({ user, next }: { user: Viewer; next: string }) {
  const [, navigate] = useLocation();
  return (
    <header className="border-b border-line pt-safe">
      <div className={cn(wrap, "flex h-16 items-center justify-between")}>
        <Link href="/" className="rounded-control" aria-label="Headcount home">
          <Logo size={22} />
        </Link>
        {user ? (
          <Button variant="ghost" size="sm" className="-mr-3" onClick={() => logOut().catch(() => {})}>
            Log out
          </Button>
        ) : user === null ? (
          <Button variant="ghost" size="sm" className="-mr-3" onClick={() => navigate(`/login?next=${encodeURIComponent(next)}`)}>
            Log in
          </Button>
        ) : null}
      </div>
    </header>
  );
}

function BackLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Link href={href} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-ink underline-offset-4 hover:underline">
      <ArrowLeft weight="bold" className="size-4" aria-hidden />
      {children}
    </Link>
  );
}

function Retry({ onRetry }: { onRetry: () => void }) {
  return (
    <EmptyState
      title="Your application didn't load."
      text="Check your connection and try again."
      action={
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      }
    />
  );
}

function Row({ href, label, sub, end }: { href: string; label: string; sub: ReactNode; end?: ReactNode }) {
  return (
    <li>
      <Link
        href={href}
        className="group flex items-center gap-4 px-4 py-4 transition-[background-color] duration-150 hover:bg-primary-soft sm:px-5"
      >
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[16px] font-semibold text-ink transition-[color] duration-150 group-hover:text-primary-ink">{label}</span>
          <span className="text-[14px] text-muted">{sub}</span>
        </span>
        {end}
        <ArrowRight weight="bold" className="size-[18px] shrink-0 text-primary transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden />
      </Link>
    </li>
  );
}

/** The mark's two bars, orange and pale blue so they read on the primary band, drawn large and cropped by its edges. */
function BandMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 40 40" aria-hidden className={cn("pointer-events-none absolute", className)}>
      {MARK.parts.map((p, i) => (
        <path key={p.d} d={p.d} fill={i ? "var(--primary-tint)" : "var(--accent)"} />
      ))}
    </svg>
  );
}

function JobList({ apps }: { apps: CareerApplication[] }) {
  const open = ROLES.filter((r) => r.open);
  return (
    <>
      <div className="overflow-hidden bg-primary">
        <div className={cn(wrap, "relative py-10 md:py-14")}>
          <BandMark className="-right-14 top-1/2 size-[200px] -translate-y-1/2 sm:-right-[27px] md:-right-[52px] md:size-[300px]" />
          <h1 className="font-display text-[36px] leading-[1.05] tracking-[-0.035em] text-white md:text-[48px]">Careers</h1>
          <p className="mt-2 text-[15px] text-white/80">
            {open.length} open {open.length === 1 ? "role" : "roles"}
          </p>
        </div>
      </div>
      <div className={wrap}>
        {apps.length > 0 && (
          <section className="mt-10">
            <h2 className={heading}>Your applications</h2>
            <ul className="mt-3 divide-y divide-line overflow-hidden rounded-card border border-line">
              {apps.map((x) => (
                <Row
                  key={x.role}
                  href={`/careers/${x.role}`}
                  label={roleOf(x.role)?.title ?? x.role}
                  sub={`Applied ${day(x.createdAt)}`}
                  end={<StatusBadge tone={STATUS[x.status].tone}>{STATUS[x.status].label}</StatusBadge>}
                />
              ))}
            </ul>
          </section>
        )}
        <section className="mt-10">
          <h2 className={heading}>Open roles</h2>
          {open.length ? (
            <ul className="mt-3 flex flex-col gap-3 *:overflow-hidden *:rounded-card *:border *:border-line">
              {open.map((r) => (
                <Row key={r.slug} href={`/careers/${r.slug}`} label={r.title} sub={<Facts parts={[r.location, r.employmentType, r.pay]} />} />
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-[15px] text-ink-soft">There are no open roles right now.</p>
          )}
        </section>
      </div>
    </>
  );
}

/**
 * `app` is undefined while a signed-in user's applications load, so Apply doesn't flash before their application shows.
 * Before the session is known, as in the static HTML, Apply shows.
 */
function Posting({ role, app, onChange }: { role: Role; app: CareerApplication | null | undefined; onChange: (a: CareerApplication) => void }) {
  const canApply = !app && role.open;
  const apply = (
    <Link href={`/careers/${role.slug}/apply`} className={cn(applyLink, app === undefined && "invisible")}>
      Apply for this job
    </Link>
  );
  return (
    <article>
      <BackLink href="/careers">All jobs</BackLink>
      <h1 className={cn(display, "mt-5")}>{role.title}</h1>
      <p className="mt-2 text-[15px] text-muted">
        <Facts parts={[role.location, role.employmentType, role.department, role.pay, role.hours, role.dates]} />
      </p>

      {app ? (
        <Yours role={role} app={app} onChange={onChange} />
      ) : !role.open ? (
        <p className="mt-6 text-[15px] text-muted">This job is no longer accepting applications.</p>
      ) : (
        <div className="mt-6 sm:max-w-[240px] lg:hidden">{apply}</div>
      )}

      <div className="mt-10 grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-16">
        <div className="max-w-[680px]">
          <Prose title="About Headcount" paragraphs={role.aboutCompany} />
          <Prose title="About the role" paragraphs={role.aboutRole} />
          <Bullets title="What you'll do" items={role.work} />
          <Bullets title="What we're looking for" items={role.lookingFor} />
          <Bullets title="Nice to have" items={role.niceToHave} />
          <Prose title="Compensation" paragraphs={role.compensation} />
          {canApply && <div className="mt-10 sm:max-w-[240px] lg:hidden">{apply}</div>}
          <Legal />
        </div>
        <aside className="hidden lg:block">
          <div className="sticky top-8 rounded-card border border-line p-6">
            <dl className="flex flex-col gap-4">
              {meta(role).map(([k, val]) => (
                <div key={k} className="flex flex-col gap-0.5">
                  <dt className="text-[13px] text-muted">{k}</dt>
                  <dd className="text-[15px] font-semibold text-ink">{val}</dd>
                </div>
              ))}
            </dl>
            {canApply && <div className="mt-6 border-t border-line pt-6">{apply}</div>}
          </div>
        </aside>
      </div>
    </article>
  );
}

function Prose({ title, paragraphs }: { title: string; paragraphs: string[] }) {
  if (!paragraphs.length) return null;
  return (
    <section className="mt-10 first:mt-0">
      <h2 className={heading}>{title}</h2>
      {paragraphs.map((p) => (
        <p key={p} className="mt-3 text-[16px] leading-relaxed text-ink-soft">
          {p}
        </p>
      ))}
    </section>
  );
}

function Bullets({ title, items }: { title: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <section className="mt-10">
      <h2 className={heading}>{title}</h2>
      <ul className="mt-3 flex flex-col gap-2">
        {items.map((it) => (
          <li key={it} className="flex gap-3 text-[16px] leading-relaxed text-ink-soft">
            <span aria-hidden className="mt-[0.75em] h-[3px] w-3 shrink-0 -skew-y-[18deg] bg-accent" />
            {it}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Legal() {
  return (
    <section className="mt-12 border-t border-line pt-6 text-[13px] leading-relaxed text-muted">
      <h2 className="text-[14px] font-semibold text-ink-soft">Equal opportunity</h2>
      <p className="mt-2">
        Headcount is an equal opportunity employer. We consider all applicants without regard to race, color, religion, sex, sexual orientation, gender
        identity, national origin, age, disability, veteran status, or any other status protected by law.
      </p>
      <p className="mt-2">Employment with Headcount is at will. Either you or Headcount can end it at any time, with or without cause or notice.</p>
      <p className="mt-2">Applicants must be legally authorized to work in the United States.</p>
      <p className="mt-2">
        Your application is used only to consider you for this role. If you apply while signed in to Headcount, it's deleted when you delete your account.
      </p>
    </section>
  );
}

function Yours({ role, app, onChange }: { role: Role; app: CareerApplication; onChange: (a: CareerApplication) => void }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const status = STATUS[app.status];

  const withdraw = async () => {
    setBusy(true);
    try {
      onChange(await withdrawApplication(role.slug));
      setAsking(false);
      toast("Application withdrawn.", "plain");
    } catch (e) {
      toast(errorText(e, "That didn't go through. Try again."), "bad");
    }
    setBusy(false);
  };

  return (
    <div className="mt-6 flex max-w-[680px] flex-col gap-4 rounded-card border border-line p-5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2.5">
          <h2 className="text-[16px] font-semibold text-ink">Your application</h2>
          <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
        </div>
        <p className="text-[14px] text-muted">
          Applied {day(app.createdAt)}
          {app.updatedAt !== app.createdAt && app.status === "submitted" ? `, edited ${day(app.updatedAt)}` : ""}
        </p>
      </div>
      {WITHDRAWABLE.includes(app.status) && (
        <div className="flex gap-2">
          {app.status === "submitted" && (
            <Button variant="secondary" size="sm" onClick={() => navigate(`/careers/${role.slug}/apply`)}>
              Edit
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setAsking(true)}>
            Withdraw
          </Button>
        </div>
      )}
      <Dialog open={asking} onOpenChange={setAsking} title="Withdraw your application?" description="You won't be able to apply to this role again.">
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setAsking(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={busy} onClick={withdraw}>
            Withdraw application
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

type Draft = Omit<CareerForm, "authorizedUs"> & { authorizedUs: boolean | null };

const blank: Draft = {
  fullName: "",
  phone: "",
  linkedin: "",
  github: "",
  website: "",
  school: "",
  graduation: "",
  authorizedUs: null,
  startDate: "",
  project: "",
  resumeName: "",
  updatesOptIn: false,
};

const draftOf = ({ fullName, phone, linkedin, github, website, school, graduation, authorizedUs, startDate, project, resumeName, updatesOptIn }: CareerApplication): Draft => ({
  fullName,
  phone,
  linkedin,
  github,
  website,
  school,
  graduation,
  authorizedUs,
  startDate,
  project,
  resumeName,
  updatesOptIn,
});

/** The parts of a Headcount profile that carry over: contact, links and the first school. A new record, never linked back. */
function fromProfile(p: ProfileView, user: { name?: string }): Partial<Draft> {
  const school = p.education[0];
  return {
    fullName: `${p.firstName} ${p.lastName}`.trim() || user.name || "",
    phone: p.phone,
    linkedin: p.links.linkedin ?? "",
    github: p.links.github ?? "",
    website: p.links.website ?? p.links.portfolio ?? "",
    school: school?.school ?? "",
    graduation: /^\d{4}-\d{2}$/.test(school?.end ?? "") ? school!.end! : "",
  };
}

type Problems = CareerProblems & { email?: string };

const ORDER: (keyof Problems)[] = ["fullName", "email", "phone", "resumeName", "linkedin", "github", "website", "school", "graduation", "authorizedUs", "startDate", "project"];

/** What a field the Worker refused says when the page's own checks passed it. Only the email and the PDF itself differ. */
const SERVER_PROBLEM: Partial<Record<keyof Problems, string>> = {
  email: "Enter a valid email address.",
  resumeName: "Upload your resume as a PDF.",
};

/** A required field's label: the words, then the site's orange asterisk. */
const req = (label: string) => (
  <>
    {label}
    <span aria-hidden className="ml-0.5 text-accent">
      *
    </span>
  </>
);
const opt = (label: string) => (
  <>
    {label} <span className="font-normal text-muted">(optional)</span>
  </>
);

/**
 * The application form, signed in or out. Signed in, it starts from the user's profile and saves through the account.
 * Signed out (`user` null), the applicant types their email, the picked resume stays in the browser, and the whole form
 * goes in one request; a plain confirmation then replaces it, since there is no application to show afterwards.
 */
function ApplyForm({
  role,
  user,
  existing,
  onDone,
}: {
  role: Role;
  user: { email: string; name?: string } | null;
  existing: CareerApplication | null;
  onDone: (a: CareerApplication) => void;
}) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [draft, setDraft] = useState<Draft>(() => (existing ? draftOf(existing) : { ...blank, fullName: user?.name ?? "" }));
  const [email, setEmail] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [profileResume, setProfileResume] = useState<string | null>(null);
  const [problems, setProblems] = useState<Problems>({});
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const form = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (existing || !user) return;
    getProfile().then(
      (p) => {
        const found = fromProfile(p, user);
        setDraft((d) => {
          const out = { ...d };
          for (const [k, val] of Object.entries(found) as [keyof Draft, string][]) if (val && !d[k]) (out as Record<string, unknown>)[k] = val;
          return out;
        });
        if (p.hasResume && p.resumeName && /\.pdf$/i.test(p.resumeName)) setProfileResume(p.resumeName);
      },
      () => {},
    );
  }, [existing, user]);

  const set = <K extends keyof Draft>(k: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [k]: value }));
    if (problems[k]) setProblems((p) => ({ ...p, [k]: undefined }));
  };
  const text = (k: keyof Draft) => ({
    name: k,
    value: draft[k] as string,
    required: true,
    onChange: (e: { target: { value: string } }) => set(k, e.target.value),
  });
  const resumeName = file?.name ?? existing?.resumeName ?? profileResume ?? "";
  const blocked = draft.authorizedUs === false;

  const focus = (field: keyof Problems) => form.current?.querySelector<HTMLElement>(`[name="${field}"]`)?.focus();

  const pick = (f: File | undefined) => {
    if (picker.current) picker.current.value = "";
    if (!f) return;
    if (!/\.pdf$/i.test(f.name)) return setProblems((p) => ({ ...p, resumeName: "Upload your resume as a PDF." }));
    if (f.size > RESUME_BYTES) return setProblems((p) => ({ ...p, resumeName: "Upload a PDF under 10 MB." }));
    setFile(f);
    setProblems((p) => ({ ...p, resumeName: undefined }));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (sending || blocked) return;
    const { form: checked, problems: found } = checkCareerForm({ ...draft, authorizedUs: draft.authorizedUs ?? undefined, resumeName });
    const typed = checkCareerEmail(email);
    const all: Problems = user ? found : { ...found, email: typed.problem };
    setProblems(all);
    const first = ORDER.find((k) => all[k]);
    if (first) {
      focus(first);
      return;
    }
    setSending(true);
    if (!user) {
      try {
        await applyAsGuest(role.slug, checked, typed.email, file!);
        setSentTo(typed.email);
        scrollTo(0, 0);
      } catch (err) {
        const field = err instanceof ApiError && err.error === "bad_application" ? (err.field as keyof Problems | undefined) : undefined;
        if (err instanceof ApiError && err.status === 413) {
          setProblems({ resumeName: "Upload a PDF under 10 MB." });
          focus("resumeName");
        } else if (field) {
          setProblems({ [field]: SERVER_PROBLEM[field] ?? "Check this field." });
          focus(field);
        } else if (err instanceof ApiError && err.status === 409) toast("You've already applied to this role with this email.", "bad");
        else if (err instanceof ApiError && err.status === 429) toast("Too many applications from your connection. Try again in a few minutes.", "bad");
        else toast(errorText(err, "Your application didn't go through. Try again."), "bad");
        setSending(false);
      }
      return;
    }
    try {
      if (file || !existing) await storeCareerResume(role.slug, file);
      const saved = existing ? await editApplication(role.slug, checked) : await applyToRole(role.slug, checked);
      onDone(saved);
      toast(existing ? "Changes saved." : "Application submitted.", "ok");
      navigate(`/careers/${role.slug}`);
    } catch (err) {
      toast(errorText(err, "Your application didn't go through. Try again."), "bad");
      setSending(false);
    }
  };

  const [year = "", month = ""] = draft.graduation.split("-");
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 17 }, (_, i) => String(thisYear - 10 + i));

  if (sentTo)
    return (
      <div className="max-w-[640px]">
        <BackLink href={`/careers/${role.slug}`}>{role.title}</BackLink>
        <h1 className="mt-5 font-display text-[28px] leading-[1.15] tracking-[-0.03em] text-ink md:text-[32px]">Application submitted</h1>
        <p className="mt-3 text-[16px] leading-relaxed text-ink-soft">
          Thanks for applying to {role.title}. We'll be in touch at {sentTo}.
        </p>
      </div>
    );

  return (
    <div className="max-w-[640px]">
      <BackLink href={`/careers/${role.slug}`}>{role.title}</BackLink>
      <h1 className="mt-5 font-display text-[28px] leading-[1.15] tracking-[-0.03em] text-ink md:text-[32px]">{existing ? "Edit your application" : `Apply for ${role.title}`}</h1>
      <p className="mt-3 text-[14px] text-muted">
        <span aria-hidden className="text-accent">
          *
        </span>{" "}
        Required
      </p>

      <form ref={form} className="mt-8" onSubmit={submit} noValidate>
        <Group>
          <Field label={req("Full name")} error={problems.fullName}>
            <Input autoComplete="name" maxLength={CAREER_MAX.fullName} {...text("fullName")} />
          </Field>
          {user ? (
            <Field label={req("Email")}>
              <Input type="email" value={user.email} readOnly disabled />
            </Field>
          ) : (
            <Field label={req("Email")} error={problems.email}>
              <Input
                type="email"
                name="email"
                autoComplete="email"
                maxLength={CAREER_MAX.email}
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (problems.email) setProblems((p) => ({ ...p, email: undefined }));
                }}
              />
            </Field>
          )}
          <Field label={req("Phone")} error={problems.phone}>
            <Input type="tel" autoComplete="tel" maxLength={CAREER_MAX.phone} {...text("phone")} />
          </Field>
        </Group>

        <Group>
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold text-ink">{req("Resume/CV")}</span>
            <div
              className={cn(
                "flex min-h-16 items-center justify-between gap-4 rounded-control border border-dashed px-4 py-3",
                problems.resumeName ? "border-bad/40 bg-tone-red/40" : resumeName ? "border-line-strong bg-sunk/60" : "border-line-strong bg-surface",
              )}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                pick(e.dataTransfer.files[0]);
              }}
            >
              <input ref={picker} type="file" accept=".pdf,application/pdf" hidden onChange={(e) => pick(e.target.files?.[0])} />
              {resumeName ? (
                <div className="flex min-w-0 flex-col gap-0.5">
                  <Steps steps={[{ label: resumeName, state: "done" }]} />
                  {!file && !existing && <span className="pl-7 text-[13px] text-muted">From your Headcount profile</span>}
                </div>
              ) : (
                <span className="text-sm text-muted">PDF, up to 10 MB</span>
              )}
              <Button
                name="resumeName"
                size="sm"
                variant="secondary"
                icon={resumeName ? <ArrowsClockwise weight="bold" /> : <FileArrowUp weight="bold" />}
                onClick={() => picker.current?.click()}
              >
                {resumeName ? "Replace" : "Upload PDF"}
              </Button>
            </div>
            {problems.resumeName && (
              <p className="text-[13px] text-bad-ink" role="alert">
                {problems.resumeName}
              </p>
            )}
          </div>
          <Field label={req("LinkedIn profile")} error={problems.linkedin}>
            <Input type="url" inputMode="url" placeholder="linkedin.com/in/you" maxLength={CAREER_MAX.link} {...text("linkedin")} />
          </Field>
          <Field label={opt("GitHub")} error={problems.github}>
            <Input type="url" inputMode="url" placeholder="github.com/you" maxLength={CAREER_MAX.link} {...text("github")} required={false} />
          </Field>
          <Field label={opt("Website")} error={problems.website}>
            <Input type="url" inputMode="url" maxLength={CAREER_MAX.link} {...text("website")} required={false} />
          </Field>
        </Group>

        <Group>
          <Field label={req("School")} error={problems.school}>
            <Input maxLength={CAREER_MAX.school} {...text("school")} />
          </Field>
          <Field label={req("Graduation date")} error={problems.graduation}>
            <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-2">
              <Select name="graduation" aria-label="Graduation month" required value={month} onChange={(e) => set("graduation", `${year}-${e.target.value}`)}>
                <option value="">Month</option>
                {MONTHS.map((m, i) => (
                  <option key={m} value={String(i + 1).padStart(2, "0")}>
                    {m}
                  </option>
                ))}
              </Select>
              <Select aria-label="Graduation year" required value={year} onChange={(e) => set("graduation", `${e.target.value}-${month}`)}>
                <option value="">Year</option>
                {[...new Set([...years, ...(year ? [year] : [])])].sort().map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            </div>
          </Field>
        </Group>

        <Group>
          <YesNo
            name="authorizedUs"
            label={req("Are you legally authorized to work in the United States?")}
            value={draft.authorizedUs}
            onChange={(x) => set("authorizedUs", x)}
            error={draft.authorizedUs === null ? problems.authorizedUs : undefined}
            note={blocked ? NEEDS_US_AUTH : undefined}
          />
          <Field label={req("Earliest start date")} error={problems.startDate}>
            <Input type="date" {...text("startDate")} />
          </Field>
        </Group>

        <Group>
          <Field label={opt("Tell us about a project you've built.")} error={problems.project}>
            <Textarea maxLength={CAREER_MAX.project} rows={5} {...text("project")} />
          </Field>
          <Checkbox name="updatesOptIn" checked={draft.updatesOptIn} onChange={(x) => set("updatesOptIn", x)}>
            Keep me up to date on future job postings and roles
          </Checkbox>
        </Group>

        <div className="border-t border-line pt-8">
          <Button type="submit" size="lg" loading={sending} disabled={blocked} className="w-full sm:w-auto">
            {existing ? "Save changes" : "Submit application"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Group({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-5 border-t border-line py-8 first:border-0 first:pt-0">{children}</div>;
}

function YesNo({
  name,
  label,
  value,
  onChange,
  error,
  note,
}: {
  name: string;
  label: ReactNode;
  value: boolean | null;
  onChange: (v: boolean) => void;
  error?: string;
  note?: string;
}) {
  return (
    <fieldset className="flex flex-col gap-1.5" aria-describedby={error || note ? `${name}-note` : undefined}>
      <legend className="mb-1.5 text-[13px] font-semibold text-ink">{label}</legend>
      <div className="flex gap-2">
        {[true, false].map((v) => (
          <label
            key={String(v)}
            className={cn(
              "inline-flex h-10 min-w-20 cursor-pointer items-center justify-center rounded-control px-4 text-sm font-semibold ring-1 ring-inset transition-[background-color,color,box-shadow] duration-150 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary",
              value === v ? "bg-primary-soft text-primary-ink ring-primary/40" : error ? "bg-surface text-ink-soft ring-bad/70" : "bg-surface text-ink-soft ring-line-strong hover:bg-sunk",
            )}
          >
            <input type="radio" name={name} className="sr-only" required checked={value === v} onChange={() => onChange(v)} aria-invalid={error ? true : undefined} />
            {v ? "Yes" : "No"}
          </label>
        ))}
      </div>
      {error ? (
        <p id={`${name}-note`} className="text-[13px] text-bad-ink" role="alert">
          {error}
        </p>
      ) : note ? (
        <p id={`${name}-note`} className="mt-1 rounded-control bg-tone-amber px-3 py-2 text-[13px] text-tone-amber-ink" role="status">
          {note}
        </p>
      ) : null}
    </fieldset>
  );
}

function Checkbox({ name, checked, onChange, children }: { name: string; checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 text-sm text-ink-soft">
      <input type="checkbox" name={name} className="peer sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span
        aria-hidden
        className={cn(
          "mt-0.5 grid size-4 shrink-0 place-items-center rounded-sm transition-[background-color,box-shadow] duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-primary",
          checked ? "bg-primary text-on-primary" : "bg-surface ring-1 ring-line-strong",
        )}
      >
        {checked && <Check size={12} weight="bold" />}
      </span>
      {children}
    </label>
  );
}
